import 'antd/dist/reset.css';
import { ClickToComponent } from 'click-to-react-component';
import LoginPage from 'components/auth/login.page';
import { observer } from 'mobx-react-lite';
import ContainerRouter from 'pages';
import { getModulesQuery } from 'queries/settings.query';
import { useEffect } from 'react';
import { moduleRoutesMap, permissionModule } from 'routes';
import { useLazyQuery } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { useFeatureFlagsClient, withFeatureFlagsProvider } from '@tools/feature-flag/frontend';
import Loading from '@tools/ui/components/common/Loading';
import { AUTH_TOKEN, config } from '@tools/ui/consts';
import { useLoginEventTracker } from '@tools/ui/hooks';
import { AbilityContext, permissionStore, userStore } from '@tools/ui-stores';
import { logger } from '@tools/ui-utils/logger.util';
import { VpnMissing } from './components/auth/vpn-missing';
import { useVerifyVpn } from './hooks/useVerifyVpn';
import { getEditor } from './utils/editor.util';

const App = observer(() => {
  const [fetchModules] = useLazyQuery(getModulesQuery, {
    onCompleted: (data) => {
      permissionStore.setModules(data.moduleMany);
    },
  });
  useLoginEventTracker(userStore);
  const userLoading = permissionStore.getIsLoading();

  const { isVpnConnected, isVerifyingVpn } = useVerifyVpn();

  const { currentUser } = userStore;

  const ffClient = useFeatureFlagsClient();

  useEffect(() => {
    (async () => {
      permissionStore.initializeModulePermissions(moduleRoutesMap, permissionModule);

      try {
        await userStore.fetchCurrentUser();
      } catch (err) {
        localStorage.removeItem(AUTH_TOKEN);
        permissionStore.toggleLoading();
      }
    })();
  }, []);

  useEffect(() => {
    if (currentUser?._id) {
      fetchModules();
    }
  }, [fetchModules, currentUser?._id]);

  useEffect(() => {
    if (currentUser && ffClient) {
      ffClient.identify({
        kind: 'user',
        key: `mos-user-${currentUser.email}`,
        email: currentUser.email,
        name: currentUser.name,
      });
    }
  }, [currentUser, ffClient]);

  useEffect(() => {
    window.BRANCH_NAME = process.env.NX_REACT_APP_BRANCH_NAME || '';
    if (config.appEnv !== 'production') {
      console.log(
        `%c Current Branch: ${window.BRANCH_NAME}`,
        'background: #7849FF; color: white; padding: 2px 5px; border-radius: 3px;',
      );
    }
  }, []);

  if (!isVpnConnected && !isVerifyingVpn) {
    return <VpnMissing />;
  }

  if (userLoading || isVerifyingVpn) {
    return <Loading />;
  }

  if (currentUser) {
    const userInfo = {
      id: currentUser._id,
      name: currentUser.name,
      email: currentUser.email,
    };
    datadogRum.setUser(userInfo);
    logger.setUser(userInfo);
    return (
      <>
        {config.appEnv !== 'production' && <ClickToComponent editor={getEditor()} />}
        <AbilityContext.Provider value={permissionStore.getAbility()}>
          <ContainerRouter />
        </AbilityContext.Provider>
      </>
    );
  }

  return <LoginPage />;
});

export default withFeatureFlagsProvider(App);
